import { computed, ref } from 'vue'
import { scheduleApi } from '@/apis/schedule.api'
import { defineStore } from 'pinia'
import { getI18n, getTranslatedItems } from '@/plugins/i18n'
import { useFetchAndStoreAsync } from '@/composables/store'

export const initScheduleStoreAsync = async() => {}

export const eventTypeEnum = Object.freeze({
  PROJECT_ROOT: 0,
  ALLOCATION: 1,
  ABSENCE: 2,
})

export const ABSENCE_COLOR = '#d05ee3'
export const ABSENCE_ICON = 'mdi-island'
export const PROJECT_FALLBACK_COLOR = '#607d8b'

export const calendarViewEnum = Object.freeze({
  DAY: 0,
  THREE_DAYS: 1,
  WORK_WEEK: 2,
  WEEK: 3,
  MONTH: 4,
  SCHEDULE: 5,
})

export const calendarDragModeEnum = Object.freeze({
  CREATE: 0,
  MOVE: 1,
  RESIZE_END: 2,
})

export const timelineRowKindEnum = Object.freeze({
  PROJECT: 0,
  SERVICE: 1,
  EVENTS: 2,
  ABSENCES: 3,
})

export const timelineDragModeEnum = Object.freeze({
  MOVE: 0,
  RESIZE_START: 1,
  RESIZE_END: 2,
})

export const timelineConflictResolutionEnum = Object.freeze({
  SHIFT_ALLOCATION: 0,
  DELETE_ALLOCATION: 1,
  TRIM_ABSENCE: 2,
  DELETE_ABSENCE: 3,
})

const eventTypeLabels = {
  [eventTypeEnum.PROJECT_ROOT]: 'common.project',
  [eventTypeEnum.ALLOCATION]: 'common.allocation',
  [eventTypeEnum.ABSENCE]: 'common.staff.absence',
}

export const taskDueBucketEnum = Object.freeze({
  OVERDUE: 0,
  TODAY: 1,
  NEXT_7_DAYS: 2,
  LATER: 3,
  NO_DATE: 4,
})

export const GENERIC_TASK_ICON = 'mdi-checkbox-marked-circle-outline'

export const useScheduleStore = defineStore('schedule', () => {
  const api = scheduleApi
  const taskTypes = ref([])
  const taskChangesKey = ref(0)

  const getLocalizedEventTypes = () => {
    const i18n = getI18n()
    return Object.values(eventTypeEnum).map(id => ({ id, name: i18n.t(eventTypeLabels[id]), label: eventTypeLabels[id] }))
  }

  const fetchEventsAsync = (params) => api.getEventsAsync(params)
  const fetchEventsByServiceForProjectAsync = (projectId) => api.getEventsByServiceForProjectAsync(projectId)
  const fetchEventsByServiceAsync = (serviceId, params) => api.getEventsByServiceAsync(serviceId, params)
  const fetchEventsByServiceIdsAsync = (serviceIds, params) => api.getEventsByServiceIdsAsync(serviceIds, params)
  const fetchEventWarningsAsync = (params) => api.getEventWarningsAsync(params)
  const fetchTimelineAsync = (start, end, projectIds) => api.getTimelineAsync(start, end, projectIds)
  const fetchReplacementCandidatesAsync = (budgetDocumentRowId) => api.getReplacementCandidatesAsync(budgetDocumentRowId)
  const replaceServiceAsync = (budgetDocumentRowId, serviceId) => api.replaceServiceAsync(budgetDocumentRowId, serviceId)
  const upsertEventAsync = (event, config) => api.upsertEventAsync(event, config)

  const syncRangeAsync = (serviceId, projectId, start, end, opts) => api.syncRangeAsync(serviceId, projectId, start, end, opts)
  const updateRangeAsync = (projectId, start, end, opts) => api.updateRangeAsync(projectId, start, end, opts)
  const deleteRangeAsync = (budgetDocumentRowId, opts) => api.deleteRangeAsync(budgetDocumentRowId, opts)
  const deleteEventAsync = async(eventId, opts) => api.deleteEventAsync(eventId, opts)
  const confirmAllocationAsync = (serviceId, projectId, allocations, budgetDocumentRowId, force, config) =>
    api.confirmAllocationAsync(serviceId, projectId, allocations, budgetDocumentRowId, force, config)
  const cancelAllocationAsync = (serviceId, projectId, budgetDocumentRowId) => api.cancelAllocationAsync(serviceId, projectId, budgetDocumentRowId)

  const localizedTaskTypes = computed(() => getTranslatedItems(taskTypes.value).map(taskType => ({
    ...taskType,
    extra_fields: getTranslatedItems(taskType.extra_fields),
  })))
  const getTaskTypes = () => localizedTaskTypes.value
  const getActiveTaskTypes = () => localizedTaskTypes.value.filter(taskType => !taskType.archived)
  const getTaskTypeById = (id) => localizedTaskTypes.value.find(taskType => taskType.id === id) ?? null
  const fetchTaskTypesAsync = async() => useFetchAndStoreAsync(taskTypes, () => api.getAsync(api.taskTypesUrl), true)
  const ensureTaskTypesAsync = async() => taskTypes.value.length ? taskTypes.value : fetchTaskTypesAsync()
  const upsertTaskTypeAsync = (data) => api.upsertAsync(api.taskTypesUrl, data)
  const deleteTaskTypeAsync = (id) => api.deleteAsync(`${api.taskTypesUrl}${id}/`)
  const reorderTaskTypesAsync = (taskTypeIds) => api.reorderTaskTypesAsync(taskTypeIds)
  const fetchProjectServiceIdsAsync = async(projectId) => (await api.getEventsByServiceForProjectAsync(projectId)).data.map(entry => entry.service_id)

  const fetchTasksAsync = async(params) => (await api.getAsync(api.tasksUrl, params)).data
  const fetchTaskCountsAsync = async() => (await api.getAsync(`${api.tasksUrl}counts/`)).data
  const notifyingTaskChanges = (requestAsync) => async(...args) => {
    const response = await requestAsync(...args)
    taskChangesKey.value++
    return response
  }
  const upsertTaskAsync = notifyingTaskChanges((data) => api.upsertAsync(api.tasksUrl, data))
  const deleteTaskAsync = notifyingTaskChanges((id) => api.deleteAsync(`${api.tasksUrl}${id}/`))
  const setTaskCompletedAsync = notifyingTaskChanges((task, completed) => api.patchAsync(`${api.tasksUrl}${task.id}/`, { completed }))
  const reorderTasksAsync = notifyingTaskChanges((taskIds) => api.reorderTasksAsync(taskIds))

  const $reset = () => {
    taskTypes.value = []
  }

  return {
    api,
    calendarDragModeEnum,
    calendarViewEnum,
    eventTypeEnum,
    taskDueBucketEnum,
    taskTypes,
    taskChangesKey,
    timelineRowKindEnum,
    timelineDragModeEnum,
    timelineConflictResolutionEnum,
    getLocalizedEventTypes,
    getTaskTypes,
    getActiveTaskTypes,
    getTaskTypeById,
    fetchTaskTypesAsync,
    ensureTaskTypesAsync,
    upsertTaskTypeAsync,
    deleteTaskTypeAsync,
    reorderTaskTypesAsync,
    fetchProjectServiceIdsAsync,
    fetchTasksAsync,
    fetchTaskCountsAsync,
    upsertTaskAsync,
    deleteTaskAsync,
    setTaskCompletedAsync,
    reorderTasksAsync,
    fetchEventsAsync,
    fetchEventsByServiceForProjectAsync,
    fetchEventsByServiceAsync,
    fetchEventsByServiceIdsAsync,
    fetchEventWarningsAsync,
    fetchTimelineAsync,
    fetchReplacementCandidatesAsync,
    replaceServiceAsync,
    upsertEventAsync,
    syncRangeAsync,
    updateRangeAsync,
    deleteRangeAsync,
    deleteEventAsync,
    confirmAllocationAsync,
    cancelAllocationAsync,
    $reset,
  }
})
